'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { List, X } from '@phosphor-icons/react'
import { GalaxyLogo } from './GalaxyLogo'

interface GalaxyNavItem {
  label: string
  href: string
  external?: boolean
}

interface GalaxyNavBarProps {
  items?: GalaxyNavItem[]
  logoHref?: string
  ctaLabel?: string
  ctaHref?: string
}

interface GalaxyProduct {
  name: string
  description: string
  href: string
  illustration: string
  illustrationHover: string
}

// Copy + order taken verbatim from Figma (node 2277:30367) and the mobile nav.
const GALAXY_PRODUCTS: GalaxyProduct[] = [
  {
    name: 'Risk Adjustment',
    description: 'Enhance risk documentation, reduce administrative effort, and save costs with AI',
    href: 'https://get-galaxy.ai/risk-adjustment',
    illustration: '/media/galaxy/illus-risk-adjustment.png',
    illustrationHover: '/media/galaxy/illus-risk-adjustment-hover.png',
  },
  {
    name: 'Quality Management',
    description: 'Close care gaps faster, enhance Stars and HEDIS performance, and reduce manual effort with AI',
    href: 'https://get-galaxy.ai/quality-management',
    illustration: '/media/galaxy/illus-quality-management.png',
    illustrationHover: '/media/galaxy/illus-quality-management-hover.png',
  },
  {
    name: 'Utilization Management',
    description: 'Streamline utilization management, reduce administrative effort, and lower costs with AI',
    href: 'https://get-galaxy.ai/utilization-management',
    illustration: '/media/galaxy/illus-utilization-management.png',
    illustrationHover: '/media/galaxy/illus-utilization-management-hover.png',
  },
  {
    name: 'Platform',
    description: 'Unites risk and quality data across claims, clinical, pharmacy, labs and supplemental data.',
    href: 'https://get-galaxy.ai/platform',
    illustration: '/media/galaxy/illus-platform.png',
    illustrationHover: '/media/galaxy/illus-platform-hover.png',
  },
]

const DEFAULT_ITEMS: GalaxyNavItem[] = [
  { label: 'HOME',       href: 'https://get-galaxy.ai/',  external: true },
  { label: 'PRODUCTS',   href: '#',                       external: false },
  { label: 'RESOURCES',  href: '/galaxy/resources',       external: false },
  { label: 'INNOVACCER', href: 'https://innovaccer.com/', external: true },
]

function NavLink({ item, onClick }: { item: GalaxyNavItem; onClick?: () => void }) {
  if (item.external) {
    return <a href={item.href} className="galaxy-nav__item" onClick={onClick}>{item.label}</a>
  }
  return <Link href={item.href} className="galaxy-nav__item" onClick={onClick}>{item.label}</Link>
}

function ProductsDropdown({ onClose }: { onClose: () => void }) {
  return (
    <div className="galaxy-products-dropdown__inner">
      {GALAXY_PRODUCTS.map((p) => (
        <a key={p.name} href={p.href} className="galaxy-product-card" onClick={onClose}>
          <div className="galaxy-product-card__header">
            <div className="galaxy-product-card__title-row">
              <span className="galaxy-product-card__name">{p.name}</span>
              <span className="galaxy-product-card__arrow" aria-hidden="true">▶</span>
            </div>
            <p className="galaxy-product-card__desc">{p.description}</p>
          </div>
          <div className="galaxy-product-card__illustration">
            <img className="galaxy-product-card__illustration-img" src={p.illustration} alt="" aria-hidden="true" />
            <img className="galaxy-product-card__illustration-img galaxy-product-card__illustration-img--hover" src={p.illustrationHover} alt="" aria-hidden="true" />
          </div>
        </a>
      ))}
    </div>
  )
}

export function GalaxyNavBar({
  items = DEFAULT_ITEMS,
  logoHref = 'https://get-galaxy.ai/',
  ctaLabel = 'REQUEST A DEMO',
  ctaHref = 'https://get-galaxy.ai/request-a-demo-in',
}: GalaxyNavBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [productsOpen, setProductsOpen] = useState(false)
  const [mobileProdOpen, setMobileProdOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [navBottom, setNavBottom] = useState(0)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const headerRef = useRef<HTMLElement>(null)

  useEffect(() => { setMounted(true) }, [])

  const openProducts = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    if (headerRef.current) {
      setNavBottom(Math.round(headerRef.current.getBoundingClientRect().bottom))
    }
    setProductsOpen(true)
  }, [])

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => setProductsOpen(false), 250)
  }, [])

  return (
    <>
    <header className="galaxy-nav" ref={headerRef}>
      <div className="galaxy-nav__card">
        <div className="galaxy-nav__left">
          <a href={logoHref} className="galaxy-nav__logo" aria-label="innovaccer galaxy">
            <GalaxyLogo height={40} />
          </a>
          <nav className="galaxy-nav__menu" aria-label="Primary">
            {items.map((item) => {
              if (item.label === 'PRODUCTS') {
                return (
                  <div key="PRODUCTS" className="galaxy-nav__products-trigger"
                    onMouseEnter={openProducts}
                    onMouseLeave={scheduleClose}>
                    <button type="button" className="galaxy-nav__item galaxy-nav__item--btn"
                      aria-haspopup="true" aria-expanded={productsOpen}>
                      {item.label}
                    </button>
                  </div>
                )
              }
              return <NavLink key={item.label} item={item} />
            })}
          </nav>
        </div>

        <div className="galaxy-nav__right">
          <a href={ctaHref} className="galaxy-nav__cta galaxy-nav__cta--solid">
            <span className="galaxy-nav__cta-text">
              <span className="btn-primary-text">{ctaLabel}</span>
              <span className="btn-hover-text">{ctaLabel}</span>
            </span>
          </a>
          <button
            type="button"
            className="galaxy-nav__hamburger"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {/* Desktop products dropdown — hover-activated, renders outside the card */}
      {productsOpen && (
        <div className="galaxy-products-dropdown"
          role="dialog"
          aria-label="Products"
          onMouseEnter={openProducts}
          onMouseLeave={scheduleClose}>
          <ProductsDropdown onClose={() => setProductsOpen(false)} />
        </div>
      )}

      {/* Mobile drawer */}
      <div className={`galaxy-nav__mobile${mobileOpen ? ' is-open' : ''}`}>
        <nav className="galaxy-nav__mobile-menu" aria-label="Mobile primary">
          {items.map((item) => {
            if (item.label === 'PRODUCTS') {
              return (
                <div key="PRODUCTS" className="galaxy-nav__mobile-products">
                  <button
                    type="button"
                    className="galaxy-nav__mobile-products-btn"
                    onClick={() => setMobileProdOpen((o) => !o)}
                    aria-expanded={mobileProdOpen}
                  >
                    <span className="galaxy-nav__mobile-nav-item__label">PRODUCTS</span>
                    <span className={`galaxy-nav__mobile-products-chevron${mobileProdOpen ? ' is-open' : ''}`} aria-hidden="true">▶</span>
                  </button>
                  {mobileProdOpen && (
                    <div className="galaxy-nav__mobile-products-list">
                      {GALAXY_PRODUCTS.map((p) => (
                        <a
                          key={p.name}
                          href={p.href}
                          className="galaxy-nav__mobile-product-card"
                          onClick={() => { setMobileOpen(false); setMobileProdOpen(false) }}
                        >
                          <div className="galaxy-nav__mobile-product-card__header">
                            <span className="galaxy-nav__mobile-product-card__name">{p.name}</span>
                            <span className="galaxy-nav__mobile-product-card__arrow" aria-hidden="true">▶</span>
                          </div>
                          <p className="galaxy-nav__mobile-product-card__desc">{p.description}</p>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )
            }
            return item.external
              ? <a key={item.label} href={item.href} className="galaxy-nav__mobile-nav-item" onClick={() => setMobileOpen(false)}>
                  <span className="galaxy-nav__mobile-nav-item__label">{item.label}</span>
                </a>
              : <Link key={item.label} href={item.href} className="galaxy-nav__mobile-nav-item" onClick={() => setMobileOpen(false)}>
                  <span className="galaxy-nav__mobile-nav-item__label">{item.label}</span>
                </Link>
          })}
        </nav>

        <div className="galaxy-nav__mobile-ctas">
          <a
            href={ctaHref}
            className="galaxy-nav__cta galaxy-nav__cta--solid galaxy-nav__cta--block"
            onClick={() => setMobileOpen(false)}
          >
            <span className="galaxy-nav__cta-text">
              <span className="btn-primary-text">{ctaLabel}</span>
              <span className="btn-hover-text">{ctaLabel}</span>
            </span>
          </a>
        </div>
      </div>
    </header>

    {/* Blur backdrop — portalled to document.body, starts at nav bottom edge */}
    {mounted && productsOpen && createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          top: navBottom,
          zIndex: 999,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          background: 'rgba(0, 0, 0, 0.35)',
          pointerEvents: 'auto',
        }}
        onClick={() => setProductsOpen(false)}
        aria-hidden="true"
      />,
      document.body
    )}
  </>
  )
}

export default GalaxyNavBar
